import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuestData } from "../../hooks/useQuestData";
import { chatBG, closeIcon } from "../../appAssets";
import AudioPlayer from "../../components/AudioPlayer/AudioPlayer";
import LottieFromUrl from "../../components/LottieFromUrl/LottieFromUrl";
import HeaderView from "./components/HeaderView/HeaderView";
import TaskTile from "./components/TaskTile/TaskTile";
import SuccessPopup from "./components/SuccessPopup/SuccessPopup";
import BingoView from "./BingoView/BingoView";
import { QUEST_STRINGS, QuestData, TaskData } from "./types";
import "./QuestScreen.scss";
import breatheyeti from "../../appAssets/breatheyeti.png";
import Loader from "../../../components/UI/Loader";
import Lottie from "lottie-react";
import saveButton from "../../appAssets/jsons/saveButton.json";
import { WebLoader } from "../../components/Loader";
import { trackEvent } from "../../../mixpanel";
import { TrackingEvents } from "../../Utilities/analytics";

/**
 * Mirrors srcMobile/Screens/FunBoxScreen/QuestScreen/index.tsx
 *
 * Three internal steps (mobile renderIndex):
 *   0 — Title card (midContainer): quest title + Lottie icon, click to advance
 *   1 — Description (descriptionContainer): text + "Let's go!" + audio
 *   2 — Tasks inside descriptionContainer:
 *         - Bingo template → 3×3 BingoView
 *         - Otherwise     → 4-column TaskTile grid + Save in header (≥4 selected)
 */
function QuestScreen() {
  const navigate = useNavigate();
  const { retrieveQuestData, postQuestData } = useQuestData();

  const [questData, setQuestData] = useState<QuestData | undefined>();
  const [taskData, setTaskData] = useState<TaskData[]>([]);
  const [renderIndex, setRenderIndex] = useState(0);
  const [questType, setQuestType] = useState(false); // true = bingo
  const [descriptionAudioUrl, setDescriptionAudioUrl] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  // ── Fetch quest (mobile updateData)
  // Mobile: callbackData?.data?.data  → axios response body → nested `data` field
  const updateData = async () => {
    setIsLoading(true);
    const resp = await retrieveQuestData();
    setIsLoading(false);
    const d: QuestData | undefined = (resp as any)?.data?.data;
    if (!d) return;
    setQuestData(d);
    setTaskData(d.tasks ?? []);
    setDescriptionAudioUrl(d.descriptionAudio ?? "");
    setQuestType((d.template ?? "").toLowerCase().includes("bingo"));
  };

  useEffect(() => {
    updateData();
  }, []);

  const handleSave = async () => {
    trackEvent(TrackingEvents.quest_saved);
    const selectedTasks = taskData
      .filter((t) => t.isSelected)
      .map((t) => ({ id: t.id, title: t.title }));

    if (selectedTasks.length < 4) {
      setToastMsg(QUEST_STRINGS.selectAtLeast);
      window.setTimeout(() => setToastMsg(null), 2500);
      return;
    }

    setIsLoading(true);
    const resp = await postQuestData({
      id: questData?.id,
      title: questData?.title,
      status: "completed",
      taskData: selectedTasks,
    });
    setIsLoading(false);
    if ((resp as any)?.data) setShowSuccess(true);
  };

  const saveBingoData = async (selected: { id: number; title?: string }[]) => {
    trackEvent(TrackingEvents.bingo_saved);
    const resp = await postQuestData({
      id: questData?.id,
      title: questData?.title,
      status: "completed",
      taskData: selected,
    });
    if ((resp as any)?.data) setShowSuccess(true);
  };

  const onBackPress = () => navigate(-1);

  return (
    <div className="quest" style={{ backgroundImage: `url(${chatBG})` }}>
      {/* ── Step 0: title card (mobile middleMainView + midContainer) ── */}
      {renderIndex === 0 && (
        <>
          <HeaderView
            onBackPress={onBackPress}
            titleText={QUEST_STRINGS.newQuestText}
            textColor="#222222"
          />
          <div className="quest__middle">
            <button
              type="button"
              className="quest__mid-container"
              onClick={() => setRenderIndex(1)}
            >
              <span className="quest__mid-title">{questData?.title}</span>
              {questData?.iconUrl && (
                <LottieFromUrl
                  url={questData.iconUrl}
                  className="quest__mid-icon"
                  loop={false}
                />
              )}
            </button>
          </div>
        </>
      )}

      {/* ── Steps 1 & 2: wrapped in descriptionContainer (white card w/ purple border) ── */}
      {renderIndex !== 0 && (
        <div className="quest__description-container">
          {/* Step 1: description text + Let's go */}
          {renderIndex === 1 && (
            <>
              <HeaderView
                onBackPress={onBackPress}
                titleText={questData?.title ?? ""}
                textColor="#000000"
              />
              <p className="quest__description-text">
                {questData?.description}
              </p>
              <div className="quest__go-btn-wrap">
                <button
                  type="button"
                  className="quest__go-btn"
                  onClick={() => setRenderIndex(2)}
                >
                  {QUEST_STRINGS.letsGo}
                </button>
              </div>
              {descriptionAudioUrl && (
                <div className="quest__desc-audio">
                  <AudioPlayer
                    uri={descriptionAudioUrl}
                    autoPlay
                    variant="compact"
                  />
                </div>
              )}
            </>
          )}

          {/* Step 2: bingo */}
          {renderIndex === 2 && questType && (
            <BingoView
              title={questData?.title ?? ""}
              bingoGridArr={taskData}
              callbackData={saveBingoData}
              onClose={onBackPress}
            />
          )}

          {/* Step 2: tile grid */}
          {renderIndex === 2 && !questType && (
            <>
              <div className="quest__step2-header">
                <h1 className="quest__step2-title">{questData?.title ?? ""}</h1>
                <div className="quest__step2-actions">
                  <div
                    // type="button"
                    // className="quest__save-action"
                    onClick={handleSave}
                    // aria-label="Save"
                  >
                    <Lottie
                      animationData={saveButton}
                      loop={false}
                      autoplay={false}
                      className="quest__save-action"
                    />
                    {/* <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg> */}
                  </div>
                  <button
                    type="button"
                    className="quest__close-action"
                    onClick={onBackPress}
                    aria-label="Close"
                  >
                    <img
                      src={closeIcon}
                      alt="Close"
                      className="quest__close-icon"
                    />
                  </button>
                </div>
              </div>
              <div className="quest__tiles-wrap">
                {taskData.map((item) => (
                  <TaskTile
                    key={item.id}
                    item={item}
                    taskData={taskData}
                    setTaskData={setTaskData}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <img src={breatheyeti} alt="" className="quest__yeti" />

      {toastMsg && <div className="quest__toast">{toastMsg}</div>}

      {isLoading && <WebLoader />}

      <SuccessPopup
        showSuccessBox={showSuccess}
        setShowSuccessBox={setShowSuccess}
        handleClosePress={onBackPress}
      />
    </div>
  );
}

export default QuestScreen;
